import React from 'react';
import { string, func, bool, oneOfType } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';
import BrandIconCard from '../BrandIconCard/BrandIconCard';
// import Slider from 'react-slick';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    relatedListingsReviews,
    faturedRevievws,
    realatedList,
    currentUser,
    onUpdateFavorites = () => {},
    onupdateUserAndRefreshFavorites = () => {},
    isFavorite = false,
    isSearchCard,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const { verified_listing, location } = publicData || {};
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const { lat, lng } = listing?.attributes.geolocation || {};

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage?.[0]?.attributes?.variants
    ? Object.keys(firstImage[0].attributes.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;
  function SampleNextArrow(props) {
    const { className, style, onClick } = props;
    return (
      <div
        className={className}
        style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClick={onClick}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11 21C16.5228 21 21 16.5228 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21Z"
            fill="black"
            fillOpacity="0.5"
            stroke="black"
            strokeOpacity="0.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect x="4.5" y="4.5" width="13" height="13" stroke="black" strokeOpacity="0.5" />
          <path
            d="M9.25 14.5L12.75 11L9.25 7.5"
            stroke="white"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }

  function SamplePrevArrow(props) {
    const { className, style, onClick } = props;
    return (
      <div
        className={className}
        style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClick={onClick}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11C21 16.5228 16.5228 21 11 21Z"
            fill="black"
            fillOpacity="0.5"
            stroke="black"
            strokeOpacity="0.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect
            x="-0.5"
            y="0.5"
            width="13"
            height="13"
            transform="matrix(-1 0 0 1 17 4)"
            stroke="black"
            strokeOpacity="0.5"
          />
          <path
            d="M12.75 14.5L9.25 11L12.75 7.5"
            stroke="white"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }
  let settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    arrows: true,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    // autoPlay: true,
    // autoplaySpeed: 3000,
  };

  const favIds = currentUser?.attributes.profile.privateData.favorites ?? [];

  const listingId = currentListing?.id?.uuid || {};

  const featureReview = faturedRevievws?.filter(r => r?.listing?.id?.uuid === listingId);

  const totalfeaturedReview = featureReview?.length;
  const averagefeatureRating =
    featureReview && featureReview.length > 0
      ? (
          featureReview.reduce((sum, r) => sum + (r?.attributes?.rating || 0), 0) /
          totalfeaturedReview
        ).toFixed(1)
      : 0;

  const thisListingReviews = (relatedListingsReviews || []).filter(
    r => String(r?.listing?.id?.uuid) == String(listingId)
  );

  const totalReviews = thisListingReviews?.length || 0;
  const validRatings = thisListingReviews
    ?.map(r => r?.attributes?.rating)
    .filter(rating => rating != null && !isNaN(rating));
  const averageRating =
    validRatings && validRatings.length > 0
      ? (validRatings.reduce((sum, r) => sum + r, 0) / validRatings.length).toFixed(1)
      : '0.0';

  return (
    <>
      <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
        {verified_listing ? (
          <span className={css.verifiedBadge}>
            <BrandIconCard type="verified" />
            <FormattedMessage id="FeatureHomestay.verified" />
          </span>
        ) : null}
        <span
          className={!favIds?.includes(listing.id?.uuid) ? css.darkFav : css.favButton}
          onClick={e => {
            e.preventDefault();

            const isRemoving = favIds?.includes(listing.id.uuid);

            // Create updated favorites array
            const updatedIds = isRemoving
              ? favIds.filter(id => id !== listing.id.uuid)
              : [...favIds, listing.id.uuid];

            const data = {
              privateData: {
                favorites: updatedIds,
              },
            };

            if (isFavorite) {
              onupdateUserAndRefreshFavorites(data, listing.id.uuid);
            } else {
              onUpdateFavorites(data);
            }
          }}
        >
          {/* 177 */}
          {/* {!favIds?.includes(listing.id?.uuid)
                            ? 'Add to favorites'
                            : 'Remove from favourites'}*/}
          <BrandIconCard type="linelike" />
        </span>
        <div className={classNames(css.cardImage, { [css.cardSearchImage]: isSearchCard })}>
          <AspectRatioWrapper
            className={css.aspectRatioWrapper}
            width={aspectWidth}
            height={aspectHeight}
            {...setActivePropsMaybe}
          >
            <>
              {/* <Slider {...settings}>
                {Array.isArray(firstImage) &&
                  firstImage.map((img, index) => {
                    return (
                      <div key={index} className={css.slideWrapper}>
                        <LazyImage
                          rootClassName={css.rootForImage}
                          alt={`${title} - ${index + 1}`}
                          image={img}
                          variants={variants}
                          sizes={renderSizes}
                        />
                      </div>
                    );
                  })}
              </Slider> */}

              {firstImage && firstImage[0] && (
                <LazyImage
                  rootClassName={css.rootForImage}
                  alt={title}
                  image={firstImage[0]}
                  variants={variants}
                  sizes={renderSizes}
                />
              )}
            </>
          </AspectRatioWrapper>
        </div>
        <div className={css.cardBody}>
          <div className={css.cardRow}>
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>
            <div className={css.listingLocation}>{location?.address?.split(',')[0]}</div>
          </div>
          <div className={css.cardBottomRow}>
            <div className={css.payment}>
              <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
            </div>
            {realatedList ? (
              <div className={css.ratingCard}>
                <span className={css.ratingStar}>
                  <BrandIconCard type="star" />
                </span>
                <span className={css.ratingBy}>
                  {averageRating}/<span className={css.ratingTotalNum}>5 ({totalReviews})</span>
                </span>
              </div>
            ) : (
              <div className={css.ratingCard}>
                <span className={css.ratingStar}>
                  <BrandIconCard type="star" />
                </span>
                <span className={css.ratingBy}>
                  {averagefeatureRating || '0.0'}/
                  <span className={css.ratingTotalNum}>5 ({totalfeaturedReview || 0})</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </NamedLink>
    </>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]).isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
